import type { Map as MapboxMap } from "mapbox-gl";
import type { WatchSource } from "vue";
import type { FetchPageResult } from "@/composables/useInfiniteList";

interface UseGeoListSearchOptions<T> {
  getMap: () => MapboxMap | undefined;
  getRoot?: () => HTMLElement | null;
  resetWhen?: WatchSource | WatchSource[];
  fetchPage: (ctx: { page: number; signal: AbortSignal; bbox: ReturnType<typeof useAppMapBbox> }) => Promise<FetchPageResult<T>>;
}

/**
 * Geo layer over `useInfiniteList`: a map bbox drives the reset (each move/filter reloads page 1),
 * and the first bbox set (on map init) triggers the first load — hence `immediate: false`.
 */
export function useGeoListSearch<T>(options: UseGeoListSearchOptions<T>) {
  const bbox = useAppMapBbox(options.getMap);
  const bboxKey = computed(() => [bbox.nwLat.value, bbox.nwLng.value, bbox.seLat.value, bbox.seLng.value].join(","));

  const paged = useInfiniteList<T>({
    fetchPage: ({ page, signal }) => options.fetchPage({ page, signal, bbox }),
    getRoot: options.getRoot,
    immediate: false,
  });

  const callerReset = options.resetWhen ? (Array.isArray(options.resetWhen) ? options.resetWhen : [options.resetWhen]) : [];

  // Wait for the map to set a bbox before the first load: a filter ref initialising on mount would
  // otherwise fire a first, bbox-less request before the map is ready (then a second once it sets one).
  watch(
    [bboxKey, ...callerReset],
    () => {
      if (bbox.nwLat.value == null) return;
      paged.reset();
    },
    { deep: true }
  );

  return { bbox, ...paged };
}
